import { api } from './client';
import type { DraftTelemetry } from '@/hooks/useDraftTelemetry';

/**
 * IELTS Writing trainer (Feature #3a).
 *
 * Spec: docs/superpowers/specs/2026-06-23-ielts-writing-design.md
 *
 * Hosted by speaking-service; the gateway routes:
 *   - student CRUD/grading  → /api/v1/ielts/writing/*
 *   - admin/manager content → /api/v1/admin/ielts/writing-tasks*
 *
 * The axios `api` client already targets baseURL /api/v1, attaches the Bearer
 * token, and unwraps the {status,message,data} envelope (and aliases the first
 * array field of list payloads to `.items`). Ownership (X-User-ID) is injected
 * gateway-side from the JWT, so callers send nothing extra.
 *
 * This module is the single source of truth for the IELTS Writing surface and
 * is imported by BOTH the student page and the admin tasks-management page.
 */

export type WritingTaskType = 'task1' | 'task2';

/** Minimum recommended word count per task type (shown as an editor hint). */
export const MIN_WORDS: Record<WritingTaskType, number> = {
  task1: 150,
  task2: 250,
};

export type WritingTask = {
  id: string;
  task_type: WritingTaskType;
  title: string;
  prompt: string;
  image_url?: string;
  target_band?: number;
  is_active: boolean;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
};

/** Body for admin/manager create+update. PUT is partial (no field zeroing). */
export type WritingTaskInput = {
  task_type?: WritingTaskType;
  title?: string;
  prompt?: string;
  image_url?: string;
  target_band?: number;
  is_active?: boolean;
};

export type SubmissionStatus = 'grading' | 'graded' | 'failed';

/** One of the four official IELTS Writing criteria keys. */
export type CriterionKey = 'ta' | 'cc' | 'lr' | 'gra';

export type WritingErrorType = 'grammar' | 'lexical' | 'coherence' | 'task';

export type WritingError = {
  excerpt: string;
  type: WritingErrorType;
  correction: string;
  explanation: string;
};

export type WritingFeedback = {
  summary: string;
  criteria: Record<CriterionKey, string>;
  errors: WritingError[];
  improved_version?: string;
};

export type WritingSubmission = {
  id: string;
  student_id: string;
  task_id: string;
  essay_text: string;
  word_count: number;
  status: SubmissionStatus;
  band_overall?: number;
  band_ta?: number;
  band_cc?: number;
  band_lr?: number;
  band_gra?: number;
  feedback?: WritingFeedback;
  graded_at?: string;
  created_at: string;
  /** History rows are enriched with the task title for display. */
  task_title?: string;
  task_type?: WritingTaskType;
};

export type BandTrendPoint = {
  date: string;
  band: number;
};

export type WritingProgress = {
  count: number;
  avg_band: number;
  latest_band: number;
  band_trend: BandTrendPoint[];
  /** The weakest criterion key, or null if there is not enough data. */
  weakest_criterion: CriterionKey | null;
};

type ListEnvelope<T> = { items: T[] };

export const ieltsApi = {
  // ---------------------------------------------------------------------------
  // Student — /ielts/writing/*
  // ---------------------------------------------------------------------------

  /** Active writing tasks the student can attempt. */
  async listTasks() {
    const { data } = await api.get<ListEnvelope<WritingTask>>('/ielts/writing/tasks');
    const d = data as unknown as { items?: WritingTask[] };
    return (Array.isArray(d) ? d : d.items ?? []) as WritingTask[];
  },

  /**
   * Submit an essay for grading. The request is synchronous: the server waits
   * for the AI grader (~30s) and returns the graded submission (status=graded)
   * — or status=failed with the row still saved so the student can retry.
   */
  async submit(body: { task_id: string; essay_text: string; draft?: DraftTelemetry }) {
    const { data } = await api.post<WritingSubmission>('/ielts/writing/submissions', body);
    return data as WritingSubmission;
  },

  /** Own submission history (band, task title, created_at), newest first. */
  async listSubmissions() {
    const { data } = await api.get<ListEnvelope<WritingSubmission>>(
      '/ielts/writing/submissions',
    );
    const d = data as unknown as { items?: WritingSubmission[] };
    return (Array.isArray(d) ? d : d.items ?? []) as WritingSubmission[];
  },

  /** Full detail of one of the student's own submissions (ownership-checked). */
  async getSubmission(id: string) {
    const { data } = await api.get<WritingSubmission>(`/ielts/writing/submissions/${id}`);
    return data as WritingSubmission;
  },

  /** Aggregate progress: band trend + weakest criterion callout. */
  async getProgress() {
    const { data } = await api.get<WritingProgress>('/ielts/writing/progress');
    return data as WritingProgress;
  },

  // ---------------------------------------------------------------------------
  // Admin / manager — /admin/ielts/writing-tasks* (RequireRole admin|manager)
  // Imported by the admin tasks-management page.
  // ---------------------------------------------------------------------------

  async adminListTasks() {
    const { data } = await api.get<ListEnvelope<WritingTask>>('/admin/ielts/writing-tasks');
    const d = data as unknown as { items?: WritingTask[] };
    return (Array.isArray(d) ? d : d.items ?? []) as WritingTask[];
  },

  async adminGetTask(id: string) {
    const { data } = await api.get<WritingTask>(`/admin/ielts/writing-tasks/${id}`);
    return data as WritingTask;
  },

  async adminCreateTask(body: WritingTaskInput) {
    const { data } = await api.post<WritingTask>('/admin/ielts/writing-tasks', body);
    return data as WritingTask;
  },

  /** Partial update — only the provided fields change (no field zeroing). */
  async adminUpdateTask(id: string, body: WritingTaskInput) {
    const { data } = await api.put<WritingTask>(`/admin/ielts/writing-tasks/${id}`, body);
    return data as WritingTask;
  },

  async adminDeleteTask(id: string) {
    await api.delete(`/admin/ielts/writing-tasks/${id}`);
  },
};

// ===========================================================================
// IELTS Speaking trainer (Feature #3b — folds #2 voice).
//
// Spec: docs/superpowers/specs/2026-06-23-ielts-speaking-design.md
//
// Hosted by speaking-service; the gateway routes:
//   - student session/grading → /api/v1/ielts/speaking/*
//   - admin/manager content    → /api/v1/admin/ielts/speaking-cue-cards*
//
// Same axios `api` client (baseURL /api/v1, Bearer token, {status,message,data}
// unwrap + `.items` alias). Ownership (X-User-ID) is injected gateway-side from
// the JWT. The 3-part mock examiner runs: Part 1 (intro) → Part 2 (cue card) →
// Part 3 (discussion). Voice IO reuses speaking-service /ai/transcribe + /ai/speak
// via `speakingApi.transcribe` / `speakingApi.synthesize`.
//
// Imported by BOTH the student page and the admin cue-card management page.
// ===========================================================================

/** Which of the three IELTS Speaking parts a turn / session is currently in. */
export type SpeakingPart = 1 | 2 | 3;

export type SpeakingSessionStatus = 'active' | 'graded' | 'failed';

/** One of the four official IELTS Speaking criteria keys. */
export type SpeakingCriterionKey = 'fc' | 'lr' | 'gra' | 'pr';

/** Part 2 long-turn prompt (admin/manager content). */
export type SpeakingCueCard = {
  id: string;
  title: string;
  cue_text: string;
  topic?: string;
  is_active: boolean;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
};

/** Body for admin/manager create+update. PUT is partial (no field zeroing). */
export type SpeakingCueCardInput = {
  title?: string;
  cue_text?: string;
  topic?: string;
  is_active?: boolean;
};

/** A single conversation turn in a session transcript. */
export type SpeakingTurn = {
  role: 'examiner' | 'student';
  content: string;
  part: SpeakingPart;
};

export type SpeakingFeedback = {
  summary: string;
  criteria: Record<SpeakingCriterionKey, string>;
  tips: string[];
};

export type SpeakingSession = {
  id: string;
  student_id: string;
  cue_card_id?: string | null;
  part: SpeakingPart;
  turns: SpeakingTurn[];
  status: SpeakingSessionStatus;
  band_overall?: number;
  band_fc?: number;
  band_lr?: number;
  band_gra?: number;
  band_pr?: number;
  feedback?: SpeakingFeedback;
  graded_at?: string;
  created_at: string;
  /** Detail/finish responses may embed the Part 2 card used. */
  cue_card?: SpeakingCueCard | null;
};

/** Reply from start/respond: the latest examiner turn + the live part. */
export type SpeakingTurnResponse = {
  session: SpeakingSession;
  /** The examiner's newest turn (also appended to session.turns). */
  examiner: SpeakingTurn;
};

/**
 * Ephemeral credentials + session hints for a live (real-time) speaking session.
 * `client_secret` is a short-lived OpenAI Realtime secret used ONLY for the
 * WebRTC SDP exchange against `call_url`.
 */
export type RealtimeTokenResult = {
  client_secret: string;
  expires_at: number;
  model: string;
  voice: string;
  call_url: string;
  cue_card?: SpeakingCueCard | null;
  /** The "active" session row created at mint; echo it back on grade so the
   *  same row is finished (keeps the per-student hourly cap accurate). */
  session_id: string;
};

const asList = <T,>(data: unknown): T[] => {
  const d = data as { items?: T[] };
  return (Array.isArray(d) ? d : d.items ?? []) as T[];
};

export const ieltsSpeakingApi = {
  // ---------------------------------------------------------------------------
  // Student — /ielts/speaking/*
  // ---------------------------------------------------------------------------

  /** Active cue cards the student can pick (or browse) for Part 2. */
  async listCueCards() {
    const { data } = await api.get<ListEnvelope<SpeakingCueCard>>(
      '/ielts/speaking/cue-cards',
    );
    return asList<SpeakingCueCard>(data);
  },

  /**
   * Start a mock. Pass a `cue_card_id` to fix the Part 2 card, or omit it for a
   * random active card. Returns the new session plus the examiner's opening
   * Part 1 question(s).
   */
  async startSession(body?: { cue_card_id?: string }) {
    const { data } = await api.post<SpeakingSession>(
      '/ielts/speaking/sessions',
      body ?? {},
    );
    return data as SpeakingSession;
  },

  /**
   * Send the student's turn (already transcribed client-side via
   * `speakingApi.transcribe`, or typed). Returns the next examiner turn and the
   * (possibly advanced) part. Ownership-checked.
   */
  async respond(id: string, body: { text: string }) {
    const { data } = await api.post<SpeakingSession>(
      `/ielts/speaking/sessions/${id}/respond`,
      body,
    );
    return data as SpeakingSession;
  },

  /** Finish + grade the session → band + 4 criteria + tips + transcript. */
  async finishSession(id: string) {
    const { data } = await api.post<SpeakingSession>(
      `/ielts/speaking/sessions/${id}/finish`,
    );
    return data as SpeakingSession;
  },

  /** Own session history (band, status, created_at), newest first. */
  async listSessions() {
    const { data } = await api.get<ListEnvelope<SpeakingSession>>(
      '/ielts/speaking/sessions',
    );
    return asList<SpeakingSession>(data);
  },

  /** Full detail of one of the student's own sessions (ownership-checked). */
  async getSession(id: string) {
    const { data } = await api.get<SpeakingSession>(`/ielts/speaking/sessions/${id}`);
    return data as SpeakingSession;
  },

  // ---------------------------------------------------------------------------
  // Live (real-time speech-to-speech) trainer — Feature #3b-live.
  // The turn-based endpoints above remain as a fallback.
  // ---------------------------------------------------------------------------

  /**
   * Mint a SHORT-LIVED ephemeral OpenAI Realtime client secret. The browser uses
   * it only to complete the WebRTC SDP exchange — the real OPENAI_API_KEY never
   * reaches the client. Pass `cue_card_id` to fix the Part 2 card, or omit for a
   * random active one.
   */
  async realtimeToken(body?: { cue_card_id?: string }) {
    const { data } = await api.post<RealtimeTokenResult>(
      '/ielts/speaking/realtime-token',
      body ?? {},
    );
    return data as RealtimeTokenResult;
  },

  /**
   * Grade the transcript assembled from a finished live session, through the
   * same band-scoring path as the turn-based flow. Returns the graded session
   * (also stored in history).
   */
  async realtimeGrade(body: {
    session_id?: string;
    cue_card_id?: string;
    duration_seconds?: number;
    turns: SpeakingTurn[];
  }) {
    const { data } = await api.post<SpeakingSession>(
      '/ielts/speaking/realtime-grade',
      body,
    );
    return data as SpeakingSession;
  },

  // ---------------------------------------------------------------------------
  // Admin / manager — /admin/ielts/speaking-cue-cards* (RequireRole admin|manager)
  // Imported by the admin cue-card management page.
  // ---------------------------------------------------------------------------

  async adminListCueCards() {
    const { data } = await api.get<ListEnvelope<SpeakingCueCard>>(
      '/admin/ielts/speaking-cue-cards',
    );
    return asList<SpeakingCueCard>(data);
  },

  async adminGetCueCard(id: string) {
    const { data } = await api.get<SpeakingCueCard>(
      `/admin/ielts/speaking-cue-cards/${id}`,
    );
    return data as SpeakingCueCard;
  },

  async adminCreateCueCard(body: SpeakingCueCardInput) {
    const { data } = await api.post<SpeakingCueCard>(
      '/admin/ielts/speaking-cue-cards',
      body,
    );
    return data as SpeakingCueCard;
  },

  /** Partial update — only the provided fields change (no field zeroing). */
  async adminUpdateCueCard(id: string, body: SpeakingCueCardInput) {
    const { data } = await api.put<SpeakingCueCard>(
      `/admin/ielts/speaking-cue-cards/${id}`,
      body,
    );
    return data as SpeakingCueCard;
  },

  async adminDeleteCueCard(id: string) {
    await api.delete(`/admin/ielts/speaking-cue-cards/${id}`);
  },
};

// ===========================================================================
// IELTS Reading trainer (Feature #3c — auto-graded, no AI).
//
// Spec: docs/superpowers/specs/2026-06-23-ielts-reading-design.md
//
// Hosted by speaking-service; the gateway routes:
//   - student tests/grading → /api/v1/ielts/reading/*
//   - admin/manager content  → /api/v1/admin/ielts/reading-tests*
//
// Same axios `api` client (baseURL /api/v1, Bearer token, {status,message,data}
// unwrap + `.items` alias). Ownership (X-User-ID) is injected gateway-side from
// the JWT. Grading is deterministic server-side: the student-facing test
// payload NEVER includes the answer key — the `answer` field is stripped, so
// `ReadingQuestion.answer` is always absent on tests fetched for taking.
//
// Imported by BOTH the student page and the admin reading-tests management page.
// ===========================================================================

/** The mixed question types a reading test can hold (v1). */
export type ReadingQuestionType =
  | 'multiple_choice'
  | 'true_false_ng'
  | 'yes_no_ng'
  | 'sentence_completion'
  | 'short_answer'
  | 'matching_headings'
  | 'multi_select'
  | 'phrase_bank'
  | 'map_labeling';

/** The fixed enum answers for the TF/NG and Y/N/NG question families. */
export const TF_NG_OPTIONS = ['true', 'false', 'not_given'] as const;
export const YN_NG_OPTIONS = ['yes', 'no', 'not_given'] as const;

/**
 * One question inside a test. `answer` is the correct-answer key and is ONLY
 * present on the admin payloads — the student-facing GET tests/:id strips it.
 * - multiple_choice / matching_headings: `answer` = option index (number)
 * - true_false_ng: `answer` ∈ TF_NG_OPTIONS
 * - yes_no_ng: `answer` ∈ YN_NG_OPTIONS
 * - sentence_completion / short_answer: `answer` = string (case-insensitive)
 */
export type ReadingQuestion = {
  id: string;
  type: ReadingQuestionType;
  prompt: string;
  options?: string[];
  /** Correct-answer key — admin only; absent on student-facing payloads. */
  answer?: number | string;
  /** Optional accepted synonyms for text answers (admin only). */
  synonyms?: string[];
  /** multi_select: how many options to choose (e.g. 2 for "choose TWO"). */
  pick_count?: number;
  /** map_labeling / diagram prompts: an image shown with the question. */
  image_url?: string;
  /** Presentational heading above a run of questions (e.g. "Questions 14–19"). */
  group_label?: string;
  /** Instruction line under group_label. */
  group_instruction?: string;
  /** 0-based part this question belongs to in a multi-part test. */
  part_index?: number;
};

/** One part of a multi-part reading test — a labelled passage shown alone. */
export type ReadingPart = {
  label: string;
  passage_html: string;
};

export type ReadingTest = {
  id: string;
  title: string;
  passage_html: string;
  parts?: ReadingPart[];
  questions: ReadingQuestion[];
  duration_min?: number;
  is_active: boolean;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
};

/** Body for admin/manager create+update. PUT is partial (no field zeroing). */
export type ReadingTestInput = {
  title?: string;
  passage_html?: string;
  questions?: ReadingQuestion[];
  duration_min?: number;
  is_active?: boolean;
};

/** The student's answer to a single question (shape mirrors `answer`). */
export type ReadingAnswerValue = number | string;

/** Map of questionId → the student's chosen answer. */
export type ReadingAnswers = Record<string, ReadingAnswerValue>;

/** Per-question grading outcome returned with a submission. The backend resolves
 * index answers to option text, so correct_answer/student_answer are display-ready. */
export type ReadingReviewItem = {
  question_id: string;
  correct: boolean;
  student_answer?: ReadingAnswerValue;
  correct_answer: ReadingAnswerValue;
};

export type ReadingSubmission = {
  id: string;
  student_id: string;
  test_id: string;
  answers: ReadingAnswers;
  raw_score: number;
  total: number;
  band: number;
  review: ReadingReviewItem[];
  created_at: string;
  /** History/detail rows may be enriched with the test title for display. */
  test_title?: string;
};

export type ReadingProgress = {
  count: number;
  // Present-but-nullable: the backend sends null for these when count === 0
  // (Go *float64 nil -> JSON null, no omitempty).
  avg_band: number | null;
  latest_band: number | null;
  band_trend: BandTrendPoint[];
};

export const ieltsReadingApi = {
  // ---------------------------------------------------------------------------
  // Student — /ielts/reading/* (answer keys stripped server-side)
  // ---------------------------------------------------------------------------

  /** Active reading tests the student can attempt (no answer keys). */
  async listTests() {
    const { data } = await api.get<ListEnvelope<ReadingTest>>('/ielts/reading/tests');
    return asList<ReadingTest>(data);
  },

  /** A single test for taking — passage + questions, WITHOUT answer keys. */
  async getTest(id: string) {
    const { data } = await api.get<ReadingTest>(`/ielts/reading/tests/${id}`);
    return data as ReadingTest;
  },

  /**
   * Submit answers for auto-grading. The server compares to the key and returns
   * {raw_score,total,band,review}. Ownership from X-User-ID.
   */
  async submit(body: { test_id: string; answers: ReadingAnswers }) {
    // The backend binds answers as map[string]string and the index-keyed types
    // (multiple_choice / matching_headings) store the option index. Stringify
    // every value so a numeric selection (e.g. 2) doesn't fail JSON binding (400).
    const answers = Object.fromEntries(
      Object.entries(body.answers).map(([k, v]) => [k, String(v)]),
    );
    const { data } = await api.post<ReadingSubmission>('/ielts/reading/submissions', {
      test_id: body.test_id,
      answers,
    });
    return data as ReadingSubmission;
  },

  /** Own submission history (band, test title, created_at), newest first. */
  async listSubmissions() {
    const { data } = await api.get<ListEnvelope<ReadingSubmission>>(
      '/ielts/reading/submissions',
    );
    return asList<ReadingSubmission>(data);
  },

  /** Full detail of one of the student's own submissions (ownership-checked). */
  async getSubmission(id: string) {
    const { data } = await api.get<ReadingSubmission>(`/ielts/reading/submissions/${id}`);
    return data as ReadingSubmission;
  },

  /** Aggregate progress: count, avg/latest band + band trend. */
  async getProgress() {
    const { data } = await api.get<ReadingProgress>('/ielts/reading/progress');
    return data as ReadingProgress;
  },

  // ---------------------------------------------------------------------------
  // Admin / manager — /admin/ielts/reading-tests* (RequireRole admin|manager)
  // Imported by the admin reading-tests management page.
  // ---------------------------------------------------------------------------

  async adminListTests() {
    const { data } = await api.get<ListEnvelope<ReadingTest>>('/admin/ielts/reading-tests');
    return asList<ReadingTest>(data);
  },

  async adminGetTest(id: string) {
    const { data } = await api.get<ReadingTest>(`/admin/ielts/reading-tests/${id}`);
    return data as ReadingTest;
  },

  async adminCreateTest(body: ReadingTestInput) {
    const { data } = await api.post<ReadingTest>('/admin/ielts/reading-tests', body);
    return data as ReadingTest;
  },

  /** Partial update — only the provided fields change (no field zeroing). */
  async adminUpdateTest(id: string, body: ReadingTestInput) {
    const { data } = await api.put<ReadingTest>(`/admin/ielts/reading-tests/${id}`, body);
    return data as ReadingTest;
  },

  async adminDeleteTest(id: string) {
    await api.delete(`/admin/ielts/reading-tests/${id}`);
  },
};

// ===========================================================================
// IELTS Listening trainer (Feature #3d — auto-graded, no AI).
//
// Spec: docs/superpowers/specs/2026-06-23-ielts-listening-design.md
//
// Listening is Reading-WITH-AUDIO: the same deterministic grader, the same six
// question types, the same answer-key-stripping / IDOR posture — only the
// passage is swapped for an audio source (a direct file URL or a YouTube link)
// plus an OPTIONAL transcript that is shown ONLY after submit.
//
// Hosted by speaking-service; the gateway routes:
//   - student tests/grading → /api/v1/ielts/listening/*
//   - admin/manager content  → /api/v1/admin/ielts/listening-tests*
//
// Same axios `api` client (baseURL /api/v1, Bearer token, {status,message,data}
// unwrap + `.items` alias). Ownership (X-User-ID) is injected gateway-side.
//
// Reuse: the question, answer, and review TYPES are identical to Reading — we
// alias them rather than redefine, so a fix to one applies to both. Only the
// test/test-input/submission shapes differ (audio_url/audio_kind/transcript_html
// instead of passage_html).
//
// SECURITY: the student-facing take list/detail strips BOTH the answer keys AND
// transcript_html (a transcript before submit is a cheat sheet). transcript_html
// may appear only in the POST-submit response and the owner-only submission
// detail. Index answers are stringified on submit (like Reading).
//
// Imported by BOTH the student page and the admin listening-tests management page.
// ===========================================================================

/** The mixed question types a listening test can hold (identical to Reading). */
export type ListeningQuestionType = ReadingQuestionType;

/**
 * One question inside a listening test. Identical shape to ReadingQuestion:
 * `answer` is the correct-answer key and is ONLY present on the admin payloads —
 * the student-facing GET tests/:id strips it. Index types (multiple_choice /
 * matching_headings) carry the option index; the enum/text types carry a string.
 */
export type ListeningQuestion = ReadingQuestion;

/** Whether `audio_url` points at a direct media file or a YouTube video. */
export type ListeningAudioKind = 'file' | 'youtube';

/** One part of a multi-part listening test — its own labelled audio. */
export type ListeningPart = {
  label: string;
  audio_url: string;
  audio_kind: ListeningAudioKind;
};

export type ListeningTest = {
  id: string;
  title: string;
  audio_url: string;
  audio_kind: ListeningAudioKind;
  parts?: ListeningPart[];
  /** OPTIONAL study-aid transcript. Present ONLY post-submit; stripped on the
   * student take list/detail. Sanitized server-side + DOMPurify on render. */
  transcript_html?: string;
  questions: ListeningQuestion[];
  duration_min?: number;
  is_active: boolean;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
};

/** Body for admin/manager create+update. PUT is partial (no field zeroing). */
export type ListeningTestInput = {
  title?: string;
  audio_url?: string;
  transcript_html?: string;
  questions?: ListeningQuestion[];
  duration_min?: number;
  is_active?: boolean;
};

/** The student's answer to a single question (mirrors Reading). */
export type ListeningAnswerValue = ReadingAnswerValue;

/** Map of questionId → the student's chosen answer. */
export type ListeningAnswers = Record<string, ListeningAnswerValue>;

/** Per-question grading outcome (identical to Reading; index answers resolved to
 * option TEXT server-side, so correct_answer/student_answer are display-ready). */
export type ListeningReviewItem = ReadingReviewItem;

export type ListeningSubmission = {
  id: string;
  student_id: string;
  test_id: string;
  answers: ListeningAnswers;
  raw_score: number;
  total: number;
  band: number;
  review: ListeningReviewItem[];
  created_at: string;
  /** History/detail rows may be enriched with the test title for display. */
  test_title?: string;
  /** Present ONLY post-submit / on the owner-only detail — the optional
   * study-aid transcript. Absent on the take view. */
  transcript_html?: string;
};

export type ListeningProgress = {
  count: number;
  // Present-but-nullable: the backend sends null for these when count === 0.
  avg_band: number | null;
  latest_band: number | null;
  band_trend: BandTrendPoint[];
};

export const ieltsListeningApi = {
  // ---------------------------------------------------------------------------
  // Student — /ielts/listening/* (answer keys AND transcript stripped server-side)
  // ---------------------------------------------------------------------------

  /** Active listening tests the student can attempt (no answer keys, no transcript). */
  async listTests() {
    const { data } = await api.get<ListEnvelope<ListeningTest>>('/ielts/listening/tests');
    return asList<ListeningTest>(data);
  },

  /** A single test for taking — audio + questions, WITHOUT answer keys or transcript. */
  async getTest(id: string) {
    const { data } = await api.get<ListeningTest>(`/ielts/listening/tests/${id}`);
    return data as ListeningTest;
  },

  /**
   * Submit answers for auto-grading → {raw_score,total,band,review} and (post
   * submit) the optional transcript_html. Ownership from X-User-ID.
   */
  async submit(body: { test_id: string; answers: ListeningAnswers }) {
    // The backend binds answers as map[string]string and the index-keyed types
    // (multiple_choice / matching_headings) store the option index. Stringify
    // every value so a numeric selection (e.g. 2) doesn't fail JSON binding (400).
    const answers = Object.fromEntries(
      Object.entries(body.answers).map(([k, v]) => [k, String(v)]),
    );
    const { data } = await api.post<ListeningSubmission>('/ielts/listening/submissions', {
      test_id: body.test_id,
      answers,
    });
    return data as ListeningSubmission;
  },

  /** Own submission history (band, test title, created_at), newest first. */
  async listSubmissions() {
    const { data } = await api.get<ListEnvelope<ListeningSubmission>>(
      '/ielts/listening/submissions',
    );
    return asList<ListeningSubmission>(data);
  },

  /** Full detail of one of the student's own submissions (may include transcript). */
  async getSubmission(id: string) {
    const { data } = await api.get<ListeningSubmission>(
      `/ielts/listening/submissions/${id}`,
    );
    return data as ListeningSubmission;
  },

  /** Aggregate progress: count, avg/latest band + band trend. */
  async getProgress() {
    const { data } = await api.get<ListeningProgress>('/ielts/listening/progress');
    return data as ListeningProgress;
  },

  // ---------------------------------------------------------------------------
  // Admin / manager — /admin/ielts/listening-tests* (RequireRole admin|manager)
  // Imported by the admin listening-tests management page.
  // ---------------------------------------------------------------------------

  async adminListTests() {
    const { data } = await api.get<ListEnvelope<ListeningTest>>(
      '/admin/ielts/listening-tests',
    );
    return asList<ListeningTest>(data);
  },

  async adminGetTest(id: string) {
    const { data } = await api.get<ListeningTest>(`/admin/ielts/listening-tests/${id}`);
    return data as ListeningTest;
  },

  async adminCreateTest(body: ListeningTestInput) {
    const { data } = await api.post<ListeningTest>('/admin/ielts/listening-tests', body);
    return data as ListeningTest;
  },

  /** Partial update — only the provided fields change (no field zeroing). */
  async adminUpdateTest(id: string, body: ListeningTestInput) {
    const { data } = await api.put<ListeningTest>(
      `/admin/ielts/listening-tests/${id}`,
      body,
    );
    return data as ListeningTest;
  },

  async adminDeleteTest(id: string) {
    await api.delete(`/admin/ielts/listening-tests/${id}`);
  },
};

// ===========================================================================
// IELTS Diagnostic — reads each skill's latest band and, once all four exist,
// the overall IELTS band (the "level" that gates/targets the trainers).
// Backend: GET /api/v1/ielts/diagnostic (speaking-service aggregator).
// ===========================================================================

export type IeltsSkill = 'listening' | 'reading' | 'writing' | 'speaking';

export type DiagnosticSkill = {
  skill: IeltsSkill;
  /** Latest band for this skill; null if never attempted. */
  band: number | null;
  attempts: number;
  /** Weak areas to drill: question-type keys (reading/listening) or criterion
   * keys (writing ta|cc|lr|gra, speaking fc|lr|gra|pr). Empty until enough data. */
  weak: string[];
};

export type Diagnostic = {
  skills: DiagnosticSkill[];
  /** True once all four skills have a band. */
  complete: boolean;
  /** Overall IELTS band; set only when complete. */
  overall_band: number | null;
};

/** Route each skill goes to in the trainers section. */
export const IELTS_SKILL_ROUTE: Record<IeltsSkill, string> = {
  listening: '/app/ielts/listening',
  reading: '/app/ielts/reading',
  writing: '/app/ielts/writing',
  speaking: '/app/ielts/speaking',
};

export const ieltsDiagnosticApi = {
  async get() {
    const { data } = await api.get<Diagnostic>('/ielts/diagnostic');
    return data;
  },
};

// ─────────────────────────────────────────────────────────────────────
// Тренажёр (задача 6-7 плана адаптивного IELTS)
//
// Отличается от тестов и спринтов: у тренировки нет фиксированной длины.
// Ученик получает задание, сразу видит разбор, получает следующее — и так
// пока не нажмёт «Завершить». Задания подбираются по слабым и неизвестным
// типам, проверка ответа идёт только на сервере.
// ─────────────────────────────────────────────────────────────────────

export type TrainerSkill = 'reading' | 'listening' | 'writing' | 'speaking';

/** Состояние типа задания в картине ученика. */
export type TypeState = 'unknown' | 'weak' | 'strong';

export type TrainerQuestion = {
  id: string;
  prompt: string;
  options?: string[];
  image_url?: string;
};

export type TrainerTask = {
  session_id: string;
  question: TrainerQuestion;
  type: string;
  state: TypeState;
  /** Почему система дала именно это задание — показывается ученику. */
  reason: string;
};

export type TrainerVerdict = {
  correct: boolean;
  correct_answer: string;
  explanation?: string;
  /** Дословная опора из текста или транскрипта. Может отсутствовать. */
  evidence?: string;
  /** Где эта опора: «Абзац C» или тайм-код. */
  evidence_ref?: string;
  done: number;
  correct_count: number;
};

export type TypeChange = {
  type: string;
  before: TypeState;
  after: TypeState;
  attempts: number;
  comment: string;
};

export type TrainerSummary = {
  total: number;
  correct: number;
  accuracy: number;
  duration_sec: number;
  type_changes: TypeChange[] | null;
};

export const trainerApi = {
  /**
   * Следующее задание по навыку. Бросает ошибку со статусом 404, когда
   * невиданных заданий не осталось — это нормальное состояние, а не сбой:
   * страница показывает «пул пополняется».
   */
  async next(skill: TrainerSkill): Promise<TrainerTask> {
    const { data } = await api.get<TrainerTask>(`/ielts/trainer/${skill}/next`);
    return data;
  },

  async answer(sessionId: string, questionId: string, answer: string): Promise<TrainerVerdict> {
    const { data } = await api.post<TrainerVerdict>('/ielts/trainer/answer', {
      session_id: sessionId,
      question_id: questionId,
      answer,
    });
    return data;
  },

  async finish(sessionId: string): Promise<TrainerSummary> {
    const { data } = await api.post<TrainerSummary>('/ielts/trainer/finish', {
      session_id: sessionId,
    });
    return data;
  },
};

// =============================================================
// Блочный тренажёр
// =============================================================
//
// Тренировка идёт блоками, как настоящий экзамен: одна запись или один текст,
// внутри несколько заданий разных типов, в каждом несколько вопросов.
// Поштучная выдача (trainerApi.next) осталась только для совместимости.

export interface UnitSource {
  title: string;
  audio_url?: string;
  duration_sec?: number;
  body?: string;
}

export interface UnitQuestion {
  id: string;
  prompt: string;
  options?: string[];
  image_url?: string;
  /** Место вопроса в задании — пропуск {{N}} вёрстки (T3, 28.10.2026). */
  slot?: number;
}

export interface UnitGroup {
  task_id: string;
  type: string;
  state: TypeState;
  reason: string;
  instruction?: string;
  group_label?: string;
  options?: string[];
  questions: UnitQuestion[];
  /** Вёрстка как на экзамене — как у мока (lib/serverLayout.ts). */
  layout?: unknown;
}

export interface UnitPrompt {
  id: string;
  kind?: string;
  part?: number;
  topic?: string;
  text: string;
  image_url?: string;
  bullets?: string[];
  state: TypeState;
  reason: string;
}

export interface TrainerUnit {
  session_id: string;
  skill: TrainerSkill;
  source?: UnitSource;
  groups?: UnitGroup[];
  prompt?: UnitPrompt;
  reason: string;
  /**
   * Блок из повтора (T3, решение владельца 30.10.2026: «задания кончились» ученику не показываем) —
   * экран ставит пометку. Сервер шага 6 ставит поле сам; прежний сервер его не знает, тогда пометку
   * берём из того, что просили (`next(skill, repeat)`).
   */
  repeat?: UnitRepeat;
}

/** Повтор: задания с ошибками, затем пройденные раньше (самые давние). */
export type UnitRepeat = 'mistakes' | 'passed';

export interface UnitReviewItem {
  question_id: string;
  type: string;
  prompt: string;
  given: string;
  correct_answer: string;
  correct: boolean;
  explanation?: string;
  evidence?: string;
  evidence_ref?: string;
}

export interface UnitResult {
  total: number;
  correct: number;
  accuracy: number;
  review: UnitReviewItem[];
  transcript?: string;
  by_type: Record<string, { total: number; correct: number }>;
}

export const trainerUnitApi = {
  /**
   * Блок целиком. Бросает ApiError со статусом 409, если ученик ещё не сдавал
   * мок, и 404, когда давать нечего (по этой ступени цепочки — `lib/trainerLoad.ts`).
   * `repeat` — повтор: `mistakes` сервер понимает с 30.09, `passed` — с шага 6 (прежний
   * сервер незнакомое значение не читает и отвечает как без него).
   */
  async next(skill: TrainerSkill, repeat?: UnitRepeat): Promise<TrainerUnit> {
    const { data } = await api.get<TrainerUnit>(`/ielts/trainer/${skill}/unit`, repeat ? { params: { repeat } } : undefined);
    return data;
  },

  /** Проверка всего блока на сервере. Пропущенные вопросы считаются неверными. */
  async submit(sessionId: string, answers: { question_id: string; answer: string }[]): Promise<UnitResult> {
    const { data } = await api.post<UnitResult>('/ielts/trainer/unit/submit', {
      session_id: sessionId,
      answers,
    });
    return data;
  },
};

/**
 * TrainerHandoff — задание, выбранное тренажёром и переданное на экран ответа.
 *
 * Тренажёр подбирает задание по слабому типу ученика. Если после этого
 * показать каталог заданий, выбор тренажёра пропадает, и вся адаптивность
 * превращается в декорацию: ученик видел одно задание, а отвечает на другое.
 * Поэтому задание передаётся вместе с переходом, а каталог не показывается.
 */
export interface TrainerHandoff {
  id: string;
  kind?: string;        // письмо: task1_academic | task1_general | task2
  part?: number;        // речь: 1 | 2 | 3
  title: string;
  text: string;
  image_url?: string;
  cue_card_id?: string; // речь: карточка Part 2, если тренажёр выбрал её
}
